import React, { useState, useEffect, useRef } from 'react'; // <-- ДОБАВЛЕНЫ useState, useEffect, useRef
import { useNavigate } from 'react-router-dom';

// Импортируй изображение акулы. Убедись, что файл лежит в src/assets/shark-mascot.png
import logoImg from '../assets/Logo.png';
import sharkMascot from '../assets/shark-mascot.png';

// Интерфейс для пузырька (вынесен наружу)
interface Bubble {
  id: number;
  x: number;
  y: number;
  size: number;
}

const CLANS = [
  {
    id: 1,
    name: 'Акулы',
    emoji: '🦈',
    color: '#00d2ff',
    description: 'Хищники океана. Быстрые, умные, беспощадные.',
    trait: 'Интеллект'
  },
  {
    id: 2,
    name: 'Слоны',
    emoji: '🐘',
    color: '#ff9966',
    description: 'Мудрые гиганты. Сила в единстве и памяти поколений.',
    trait: 'Мудрость'
  },
  {
    id: 3,
    name: 'Гориллы',
    emoji: '🦍',
    color: '#71B280',
    description: 'Могучие воины. Лидеры стаи с несгибаемой волей.',
    trait: 'Сила'
  },
  {
    id: 4,
    name: 'Волки',
    emoji: '🐺',
    color: '#a8a9ad',
    description: 'Верные охотники. Работают в команде, побеждают вместе.',
    trait: 'Верность'
  },
];

const FEATURES = [
  {
    icon: '📰',
    title: 'Лента новостей',
    description: 'Делись мыслями со всем миром или только со своей стаей'
  },
  {
    icon: '💬',
    title: 'Личные сообщения',
    description: 'Общайся с друзьями в реальном времени'
  },
  {
    icon: '🏆',
    title: 'Рейтинг и достижения',
    description: 'Повышай свой статус активностью и получай награды'
  },
  {
    icon: '⚔️',
    title: 'Клановые битвы',
    description: 'Соревнуйся с другими стаями за первенство'
  },
  {
    icon: '🛒',
    title: 'Магазин стикеров',
    description: 'Выражай эмоции уникальными стикерами'
  },
  {
    icon: '️️🛡️️',
    title: 'Безопасность',
    description: 'Администрация следит за порядком в сообществе'
  },
];

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  // --- ЛОГИКА ПУЗЫРЬКОВ (СТРОГО ВНУТРИ КОМПОНЕНТА) ---
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const bubbleIdCounter = useRef(0);
  const lastBubbleTime = useRef(0);

  // Эффект создания пузырьков при движении мыши
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const now = Date.now();
      if (now - lastBubbleTime.current > 50) { // Ограничение частоты (раз в 50мс)
        const newBubble: Bubble = {
          id: bubbleIdCounter.current++,
          x: e.clientX,
          y: e.clientY,
          size: Math.random() * 12 + 4, // Размер от 4 до 16px
        };

        setBubbles(prev => [...prev.slice(-20), newBubble]); // Храним макс. 20 штук
        lastBubbleTime.current = now;
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Эффект удаления старых пузырьков
  useEffect(() => {
    const interval = setInterval(() => {
      setBubbles(prev => prev.slice(1));
    }, 800);
    return () => clearInterval(interval);
  }, []);
  // -------------------------------------------------------

  return (
    <div className="min-h-screen bg-[#0f1115] text-white overflow-x-hidden relative cursor-default">

      {/* CSS анимации */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(-5deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        .animate-float { animation: float 4s ease-in-out infinite; }
        
        @keyframes rise {
          0% { transform: translateY(0) scale(1); opacity: 0.7; }
          100% { transform: translateY(-120px) scale(1.8); opacity: 0; }
        }
        .bubble-anim { 
          animation: rise 0.9s ease-out forwards; 
          pointer-events: none; /* Чтобы сквозь пузыри можно было кликать */
        }
      `}</style>

      {/* Рендер пузырьков курсора */}
      {bubbles.map(bubble => (
        <div
          key={bubble.id}
          className="fixed rounded-full border border-cyan-400/60 bg-cyan-400/10 bubble-anim z-[90]"
          style={{
            left: bubble.x,
            top: bubble.y,
            width: bubble.size,
            height: bubble.size,
            transform: 'translate(-50%, -50%)'
          }}
        />
      ))}

      {/* Навигация */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0f1115]/80 backdrop-blur-md border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400 uppercase">
              SHARKLY
            </h1>
            <span className="text-2xl"> <img src={logoImg} alt="Sharkly Footer Logo" className="h-8 w-auto object-contain opacity-80 hover:opacity-100 transition" />
            </span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-2 rounded-full font-bold text-sm text-gray-300 hover:text-white transition border border-gray-700 hover:border-gray-500"
            >
              Войти
            </button>
            <button
              onClick={() => navigate('/register')}
              className="px-6 py-2 rounded-full font-bold text-sm bg-blue-600 hover:bg-blue-500 text-white transition shadow-[0_0_20px_rgba(37,99,235,0.4)]"
            >
              Регистрация
            </button>
          </div>
        </div>
      </nav>

      {/* Hero секция */}
      <section className="relative min-h-screen flex items-center justify-center px-6 pt-20 pb-10">
        {/* Фоновые элементы */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        {/* КОНТЕЙНЕР ДЛЯ КОНТЕНТА: Flexbox для разделения на текст и акулу */}
        <div className="relative z-10 max-w-7xl mx-auto w-full flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-0">

          {/* ЛЕВАЯ ЧАСТЬ: Текст и кнопки */}
          <div className="flex-1 text-center lg:text-left order-2 lg:order-1">
            <div className="inline-block mb-6 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-400 text-sm font-bold">
                Добро пожаловать в стаю
            </div>

            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter mb-6 leading-none">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-emerald-400 to-blue-400">
                SHARKLY
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-400 mb-4 font-light">
              Социальная сеть для настоящих хищников
            </p>

            <p className="text-base md:text-lg text-gray-500 mb-12 max-w-2xl mx-auto lg:mx-0">
              Выбери свою стаю, общайся, делись мыслями, повышай рейтинг и становись легендой Sharkly
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12 lg:mb-20">
              <button
                onClick={() => navigate('/register')}
                className="px-8 py-4 rounded-full font-bold text-lg bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-500 hover:to-emerald-500 text-white transition shadow-[0_0_30px_rgba(37,99,235,0.5)] hover:-translate-y-1 active:scale-95"
              >
                 Вступить в стаю
              </button>
              <button
                onClick={() => navigate('/login')}
                className="px-8 py-4 rounded-full font-bold text-lg bg-[#1a1d24] hover:bg-[#252830] text-white transition border border-gray-700 hover:border-gray-500"
              >
                У меня уже есть аккаунт
              </button>
            </div>

            {/* Статистика */}
            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto lg:mx-0 border-t border-gray-800 pt-10">
              <div className="text-center lg:text-left">
                <div className="text-3xl md:text-4xl font-black text-blue-400">4</div>
                <div className="text-sm text-gray-500 mt-1 uppercase tracking-wider">Мощные клана</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-3xl md:text-4xl font-black text-emerald-400">∞</div>
                <div className="text-sm text-gray-500 mt-1 uppercase tracking-wider">Возможностей</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-3xl md:text-4xl font-black text-purple-400">24/7</div>
                <div className="text-sm text-gray-500 mt-1 uppercase tracking-wider">Активность</div>
              </div>
            </div>
          </div>

          {/* ПРАВАЯ ЧАСТЬ: МАСКОТ АКУЛЫ */}
          <div className="flex-1 flex justify-center lg:justify-end order-1 lg:order-2 mb-8 lg:mb-0">
             <div className="relative group">
                {/* Декоративное свечение позади акулы */}
                <div className="absolute inset-0 bg-blue-500/20 rounded-full blur-[100px] scale-75"></div>

                <img
                  src={sharkMascot}
                  alt="Sharkly Mascot"
                  className="w-64 md:w-80 lg:w-[450px] xl:w-[550px] relative z-10 animate-float drop-shadow-[0_20px_30px_rgba(0,150,255,0.3)] transition-transform duration-500 group-hover:scale-105"
                />
             </div>
          </div>

        </div>
      </section>

      {/* Секция кланов */}
      <section className="py-24 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-4">
              Выбери свою <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">стаю</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Четыре могущественных клана. Каждый со своим характером и силой. К кому присоединишься ты?
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {CLANS.map((clan) => (
              <div
                key={clan.id}
                className="group relative bg-[#1a1d24] rounded-3xl p-8 border border-gray-800 hover:border-gray-600 transition-all duration-300 hover:-translate-y-2 overflow-hidden"
              >
                {/* Фоновый градиент */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                  style={{ background: `radial-gradient(circle at center, ${clan.color}, transparent)` }}
                />

                <div className="relative z-10">
                  <div className="text-7xl mb-4 group-hover:scale-110 transition-transform duration-300">
                    {clan.emoji}
                  </div>

                  <h3 className="text-2xl font-black mb-2" style={{ color: clan.color }}>
                    {clan.name}
                  </h3>

                  <div className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-4 border"
                    style={{
                      backgroundColor: `${clan.color}15`,
                      borderColor: `${clan.color}40`,
                      color: clan.color
                    }}
                  >
                    {clan.trait}
                  </div>

                  <p className="text-gray-400 text-sm leading-relaxed">
                    {clan.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Секция возможностей — ИСПРАВЛЕНО: убран bg-[#0a0c10] для единого фона */}
      <section className="py-24 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-4">
              Что тебя <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-400">ждёт</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Sharkly — это не просто соцсеть. Это целая экосистема для общения и соревнований
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, i) => (
              <div
                key={i}
                className="bg-[#1a1d24] rounded-2xl p-6 border border-gray-800 hover:border-blue-500/30 transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="text-5xl mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-2 text-white">
                  {feature.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

            {/* CTA секция — ИСПРАВЛЕНО: убран градиентный оверлей для единого фона */}
      <section className="py-24 px-6 relative">
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-6">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">Готов</span> стать частью стаи?
          </h2>

          <p className="text-gray-400 text-lg mb-12 max-w-2xl mx-auto">
            Присоединяйся к тысячам пользователей Sharkly. Регистрация занимает меньше минуты.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/register')}
              className="px-10 py-4 rounded-full font-bold text-lg bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-500 hover:to-emerald-500 text-white transition shadow-[0_0_30px_rgba(37,99,235,0.5)] hover:-translate-y-1 active:scale-95"
            >
               Создать аккаунт
            </button>
            <button
              onClick={() => navigate('/login')}
              className="px-10 py-4 rounded-full font-bold text-lg bg-[#1a1d24] hover:bg-[#252830] text-white transition border border-gray-700 hover:border-gray-500"
            >
              Войти в аккаунт
            </button>
          </div>
        </div>
      </section>

      {/* Футер — ИСПРАВЛЕНО: убран bg-[#0a0c10] для единого фона */}
      <footer className="py-12 px-6 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400 uppercase">
                SHARKLY🦈
              </h1>
              <span className="text-2xl"> </span>
            </div>

            <div className="flex gap-6 text-sm text-gray-500">
              <button onClick={() => navigate('/rules')} className="hover:text-white transition">Правила</button>
              <button onClick={() => navigate('/contact')} className="hover:text-white transition">Связь</button>
              <button onClick={() => navigate('/sponsorship')} className="hover:text-white transition">Спонсорство</button>
            </div>

            <div className="text-sm text-gray-600">
              © 2026 Sharkly. Все права защищены.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;